// The person's own page, arranged by them.
//
// Everything above the widgets is identity: a banner, an avatar whose size they
// choose, a name, a tagline, their weekend in numbers, and a row of links.
// Below it is a grid of blocks they can reorder, resize, restyle and hide, and
// the arrangement is stored on the account so it follows them between devices.
//
// Signed out, the page still works. It shows the plan in this browser and says
// what an account would add, rather than showing a stranger's placeholder.

import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '../../data/store.tsx';
import { api } from '../../data/account.ts';
import type { ArtPiece, ArtSubmission } from '../../data/account.ts';
import { useSession } from '../../data/session.tsx';
import { minutesOfMusic } from '../../engine/picks.ts';
import { PANELS } from '../profile/panels.tsx';
import { WidgetShell } from '../profile/WidgetShell.tsx';
import { ALL_WIDGETS, CATALOG, WIDGET_NAMES } from '../profile/types.ts';
import type { PanelContext, ProfilePage, Widget, WidgetId } from '../profile/types.ts';
import { FONTS, dayColor, ensureFont } from '../theme.ts';
import { Select } from '../components/Select.tsx';
import { GroupInvitation } from '../components/GroupInvitation.tsx';
import '../../styles/account-refinements.css';

const WIDTHS = [25, 34, 50, 66, 75, 100];
const STYLES: Widget['style'][] = ['panel', 'outline', 'plain'];
const CORNERS: Widget['corner'][] = ['soft', 'sharp', 'round', 'circle'];
const PHOTO_SHAPES: ProfilePage['photoShape'][] = ['rounded', 'circle', 'square'];
const TEXTURES: Widget['texture'][] = ['none', 'grain', 'glow'];
const OPACITIES = [100, 80, 60, 40, 20];

const DEFAULT_PAGE: ProfilePage = {
  aboutMd: '',
  links: [],
  widgets: [
    { id: 'about', w: 66, style: 'panel', corner: 'soft', texture: 'grain', opacity: 100 },
    { id: 'countdown', w: 34, style: 'panel', corner: 'soft', texture: 'glow', opacity: 100 },
    { id: 'weekend', w: 66, style: 'panel', corner: 'soft', texture: 'grain', opacity: 100 },
    { id: 'lineup', w: 34, style: 'outline', corner: 'soft', texture: 'none', opacity: 100 },
    { id: 'art', w: 66, style: 'panel', corner: 'soft', texture: 'grain', opacity: 100 },
    { id: 'crew', w: 34, style: 'panel', corner: 'soft', texture: 'grain', opacity: 100 },
    { id: 'photos', w: 100, style: 'panel', corner: 'soft', texture: 'grain', opacity: 100 },
  ],
  avatarSize: 84,
  accent: null,
  fontDisplay: null,
  fontBody: null,
  photoShape: 'rounded',
};

const next = <T,>(list: T[], value: T): T => list[(list.indexOf(value) + 1) % list.length];

// The link that belongs beside one block's heading. A block with nowhere to send
// anybody gets nothing rather than a link back to itself.
function asideFor(id: WidgetId): React.ReactNode {
  switch (id) {
    case 'photos':
      return (
        <Link to="/photos" className="small ghost">
          All photos
        </Link>
      );
    case 'crew':
      return (
        <Link to="/search" className="small ghost">
          Find people
        </Link>
      );
    case 'art':
      return (
        <Link to="/art" className="small ghost">
          The corner
        </Link>
      );
    case 'wanted':
      return (
        <Link to="/lineup" className="small ghost">
          Rate more
        </Link>
      );
    case 'weekend':
      return (
        <Link to="/plan" className="small ghost">
          Open the planner
        </Link>
      );
    default:
      return null;
  }
}

export function Profile() {
  const { state, derived } = useApp();
  const { session, refresh: refreshSession } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const [page, setPage] = useState<ProfilePage>(DEFAULT_PAGE);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [tagline, setTagline] = useState('');
  const [privacyBusy, setPrivacyBusy] = useState(false);
  const [privacyNote, setPrivacyNote] = useState('');
  const [saving, setSaving] = useState<string>('');
  const [lineupDay, setLineupDay] = useState<string | null>(null);
  const [dragging, setDragging] = useState<WidgetId | null>(null);
  const [dragOver, setDragOver] = useState<WidgetId | null>(null);
  const [photos, setPhotos] = useState<{ id: string; url: string; caption: string }[]>([]);
  const [art, setArt] = useState<ArtPiece[]>([]);
  const [offers, setOffers] = useState<ArtSubmission[]>([]);
  const [artNote, setArtNote] = useState('');

  useEffect(() => {
    setName(session?.user?.displayName ?? '');
  }, [session?.user?.displayName]);

  useEffect(() => {
    let cancelled = false;
    const user = session?.user;
    setTagline('');
    setPage(DEFAULT_PAGE);
    setPhotos([]);
    setArt([]);
    setOffers([]);
    setEditing(new URLSearchParams(location.search).get('edit') === 'privacy');
    setPrivacyNote('');
    if (!user) {
      return;
    }

    void (async () => {
      const [response, shelf] = await Promise.all([
        fetch('/api/profile', { credentials: 'same-origin' }),
        fetch('/api/photos?scope=mine', { credentials: 'same-origin' }),
      ]);
      if (response.ok) {
        const body = (await response.json()) as {
          profile: { bio?: string; page?: ProfilePage };
        };
        if (cancelled) return;
        setTagline(body.profile?.bio ?? '');
        if (body.profile?.page) {
          setPage({
            ...DEFAULT_PAGE,
            ...body.profile.page,
            widgets:
              body.profile.page.widgets?.length > 0
                ? body.profile.page.widgets
                : DEFAULT_PAGE.widgets,
          });
        }
      }

      if (shelf.ok) {
        const body = (await shelf.json()) as { photos?: { id: string; caption?: string }[] };
        if (cancelled) return;
        setPhotos(
          (body.photos ?? []).slice(0, 8).map((p) => ({
            id: p.id,
            url: `/api/photos/${p.id}/raw`,
            caption: p.caption ?? '',
          })),
        );
      }
    })().catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [session?.user?.id]);

  // The art and the offers load on their own, so a slow gallery never holds up
  // the identity block at the top of the page.
  const loadArt = useCallback(async () => {
    const username = session?.user?.username;
    if (!username) return;
    const [pieces, submissions] = await Promise.all([
      api.art({ artist: username }),
      api.mySubmissions(),
    ]);
    if (pieces.ok) setArt((pieces.data?.pieces ?? []).filter((p) => p.mine));
    if (submissions.ok) setOffers(submissions.data?.submissions ?? []);
  }, [session?.user?.username]);

  useEffect(() => {
    let dropped = false;
    void loadArt().then(() => {
      if (dropped) setArt([]);
    });
    return () => {
      dropped = true;
    };
  }, [loadArt]);

  const patchPrivacy = async (body: { visibility?: 'private' | 'public'; lookingForGroup?: boolean }) => {
    setPrivacyBusy(true);
    setPrivacyNote('');
    const result = await api.patchProfile(body);
    if (result.ok) await refreshSession();
    setPrivacyNote(result.ok ? 'Saved.' : result.error ?? 'That did not save.');
    setPrivacyBusy(false);
  };
  const user = session?.user;
  const crews = session?.crews ?? [];
  const me = state.members.find((m) => m.userId === state.userId);
  const displayName = (editing ? name : user?.displayName) || me?.displayName || 'You';
  const allPicks = [...derived.picksByDay.values()].flat();
  const ratedCount = state.ratings.filter(
    (r) => r.userId === state.userId && r.source === 'manual',
  ).length;

  const stats = [
    { n: allPicks.length, label: 'sets picked' },
    { n: `${Math.round(minutesOfMusic(allPicks) / 60)}h`, label: 'of music' },
    { n: ratedCount, label: ratedCount === 1 ? 'artist rated' : 'artists rated' },
    { n: state.members.length, label: 'in your crew' },
  ];

  const shown = page.widgets;
  const hidden = ALL_WIDGETS.filter((id) => !shown.some((w) => w.id === id));

  const savePage = async (nextPage: ProfilePage, extra: Record<string, unknown> = {}) => {
    setPage(nextPage);
    if (!user) return;
    setSaving('Saving');
    const response = await fetch('/api/profile', {
      method: 'PATCH',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        aboutMd: nextPage.aboutMd,
        links: nextPage.links,
        widgets: nextPage.widgets,
        avatarSize: nextPage.avatarSize,
        fontDisplay: nextPage.fontDisplay ?? '',
        fontBody: nextPage.fontBody ?? '',
        photoShape: nextPage.photoShape,
        ...extra,
      }),
    });
    setSaving(response.ok ? 'Saved' : 'That did not save');
    if (response.ok && 'displayName' in extra) await refreshSession();
    setTimeout(() => setSaving(''), 1600);
  };

  const mutate = (id: WidgetId, change: (w: Widget) => Widget) =>
    void savePage({ ...page, widgets: page.widgets.map((w) => (w.id === id ? change(w) : w)) });

  const move = (id: WidgetId, by: number) => {
    const index = page.widgets.findIndex((w) => w.id === id);
    const target = index + by;
    if (index < 0 || target < 0 || target >= page.widgets.length) return;
    const widgets = [...page.widgets];
    const [taken] = widgets.splice(index, 1);
    widgets.splice(target, 0, taken);
    void savePage({ ...page, widgets });
  };

  const dayIds = derived.dayContexts.map((c) => c.day.id);
  const activeDay = lineupDay && dayIds.includes(lineupDay) ? lineupDay : dayIds[0];

  ensureFont(page.fontDisplay);
  ensureFont(page.fontBody);
  const display = FONTS.find((f) => f.id === page.fontDisplay);
  const bodyFont = FONTS.find((f) => f.id === page.fontBody);
  const accountReady = Boolean(
    (location.state as { accountReady?: boolean } | null)?.accountReady,
  );
  const hasPlan =
    state.onboarded ||
    state.picks.some((pick) => pick.userId === state.userId) ||
    state.ratings.some((rating) => rating.userId === state.userId && rating.source === 'manual') ||
    state.notes.some((note) => note.userId === state.userId) ||
    state.mustSee.some((item) => item.userId === state.userId);
  const pageStyle: React.CSSProperties = {
    ...(page.accent ? { ['--accent' as string]: page.accent } : null),
    ...(display ? { ['--font-display' as string]: display.display } : null),
    ...(bodyFont ? { ['--font-body' as string]: bodyFont.body } : null),
    ...(bodyFont ? { fontFamily: bodyFont.body } : null),
  };

  return (
    <div className="profilepage" style={pageStyle}>
      <div className="profilebanner">
        <div className="bannerart" aria-hidden="true" />
        <div className="banneractions">
          {saving && <span className="badge">{saving}</span>}
          {user ? (
            <button className="small" onClick={() => setEditing(!editing)}>
              {editing ? 'Done' : 'Edit profile'}
            </button>
          ) : (
            <Link to="/account" state={{ from: '/profile' }} className="badge">
              Sign in
            </Link>
          )}
        </div>
        <div
          className="bigavatar"
          style={{ width: page.avatarSize, height: page.avatarSize, fontSize: page.avatarSize / 2.4 }}
          aria-hidden="true"
        >
          {displayName.slice(0, 1).toUpperCase()}
        </div>
      </div>

      <header className="profileident">
        {editing ? (
          <div className="editident">
            <div className="editgroup">
              <h3>Name and tagline</h3>
              <label className="field">
                <span>Display name</span>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Display name"
                  className="namefield"
                />
              </label>
              <label className="field">
                <span>Tagline</span>
                <input
                  value={tagline}
                  onChange={(e) => setTagline(e.target.value)}
                  placeholder="One line about your weekend"
                />
              </label>
              <button
                className="primary"
                onClick={() =>
                  void savePage({ ...page }, { displayName: name, bio: tagline })
                }
              >
                Save name and tagline
              </button>
            </div>

            <section id="privacy" className="editgroup" aria-label="Profile privacy">
              <h3>Who can see you</h3>
              <p>A public profile can publish itineraries. Making it private removes those itineraries from search.</p>
              <div className="privacy-row">
                <span>
                  Profile visibility
                  <span className="privacy-state">
                    {session?.profile ? `Currently ${session.profile.visibility}` : 'Loading'}
                  </span>
                </span>
                <button disabled={privacyBusy || !session?.profile} onClick={() => void patchPrivacy({ visibility: session?.profile?.visibility === 'public' ? 'private' : 'public' })}>
                  {session?.profile?.visibility === 'public' ? 'Make it private' : 'Make it public'}
                </button>
              </div>
              <div className="privacy-row">
                <span>
                  Looking for a crew
                  <span className="privacy-state">
                    {session?.profile ? (session.profile.lookingForGroup ? 'Shown to people searching' : 'Not shown') : 'Loading'}
                  </span>
                </span>
                <button disabled={privacyBusy || !session?.profile} onClick={() => void patchPrivacy({ lookingForGroup: !session?.profile?.lookingForGroup })}>
                  {session?.profile?.lookingForGroup ? 'Stop showing' : 'Start showing'}
                </button>
              </div>
              {privacyNote && <p role="status">{privacyNote}</p>}
            </section>

            <div className="editgroup">
              <h3>Look of this page</h3>
              <div className="flex wrap tight">
                <span className="muted">Avatar</span>
                <input
                  type="range"
                  min={56}
                  max={160}
                  step={4}
                  value={page.avatarSize}
                  aria-label="Avatar size"
                  onChange={(e) => setPage({ ...page, avatarSize: Number(e.target.value) })}
                  onMouseUp={() => void savePage(page)}
                  onTouchEnd={() => void savePage(page)}
                />
                <span className="muted">Photos</span>
                {PHOTO_SHAPES.map((s) => (
                  <button
                    key={s}
                    className={`chip ${page.photoShape === s ? 'on' : ''}`}
                    aria-pressed={page.photoShape === s}
                    onClick={() => void savePage({ ...page, photoShape: s })}
                  >
                    {s}
                  </button>
                ))}
              </div>

              <div className="flex wrap tight">
                <span className="muted">Headers</span>
                <Select
                  aria-label="Profile header font"
                  value={page.fontDisplay ?? ''}
                  onChange={(e) =>
                    void savePage({ ...page, fontDisplay: e.target.value || null })
                  }
                >
                  <option value="">Same as the site</option>
                  {FONTS.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.label}
                    </option>
                  ))}
                </Select>
                <span className="muted">Body</span>
                <Select
                  aria-label="Profile body font"
                  value={page.fontBody ?? ''}
                  onChange={(e) => void savePage({ ...page, fontBody: e.target.value || null })}
                >
                  <option value="">Same as the site</option>
                  {FONTS.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.label}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            <div className="editgroup">
              <h3>Links</h3>
              {page.links.length === 0 && <p>No links yet. Add one and it shows under your name.</p>}
              <div className="linkedit">
                {page.links.map((link, i) => (
                  <div key={i} className="flex tight">
                    <input
                      value={link.label}
                      placeholder="label"
                      aria-label={`Link ${i + 1} label`}
                      style={{ width: 120 }}
                      onChange={(e) => {
                        const links = [...page.links];
                        links[i] = { ...link, label: e.target.value };
                        setPage({ ...page, links });
                      }}
                    />
                    <input
                      value={link.url}
                      placeholder="https://"
                      aria-label={`Link ${i + 1} address`}
                      style={{ flex: 1, minWidth: 140 }}
                      onChange={(e) => {
                        const links = [...page.links];
                        links[i] = { ...link, url: e.target.value };
                        setPage({ ...page, links });
                      }}
                    />
                    <button
                      className="chip"
                      aria-label={`Remove link ${i + 1}`}
                      onClick={() =>
                        void savePage({ ...page, links: page.links.filter((_, j) => j !== i) })
                      }
                    >
                      remove
                    </button>
                  </div>
                ))}
                <div className="flex tight">
                  <button
                    className="chip"
                    onClick={() => setPage({ ...page, links: [...page.links, { label: '', url: '' }] })}
                  >
                    + add link
                  </button>
                  {page.links.length > 0 && (
                    <button className="chip" onClick={() => void savePage(page)}>
                      Save links
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="editgroup">
              <h3>Blocks on the page</h3>
              <p>
                {hidden.length === 0
                  ? 'Every block is on the page. Hide one from its own corner to bring it back here.'
                  : 'Blocks you have not put on the page yet.'}
              </p>
              <div className="blockoffer">
                {hidden.map((id) => {
                  const entry = CATALOG.find((c) => c.id === id);
                  return (
                    <button
                      key={id}
                      className="blockcard"
                      onClick={() =>
                        void savePage({
                          ...page,
                          widgets: [
                            ...page.widgets,
                            {
                              id,
                              w: entry?.width ?? 50,
                              style: 'panel',
                              corner: 'soft',
                              texture: 'grain',
                              opacity: 100,
                            },
                          ],
                        })
                      }
                    >
                      <b>+ {WIDGET_NAMES[id]}</b>
                      <span className="sub">{entry?.blurb ?? ''}</span>
                    </button>
                  );
                })}
              </div>
              <p className="muted small">
                On each block: move, resize, change the box, corners, texture and opacity, or hide
                it.
              </p>
            </div>
          </div>
        ) : (
          <>
            <h1>{displayName}</h1>
            <p className="muted">
              {user
                ? tagline || `@${user.username}`
                : 'Not signed in. This plan lives in this browser until you make an account.'}
            </p>
            <div className="statrow">
              {stats.map((s) => (
                <span key={s.label}>
                  <b>{s.n}</b> {s.label}
                </span>
              ))}
            </div>
            {page.links.length > 0 && (
              <div className="chiprow">
                {page.links.map((l) => (
                  <a key={l.url} className="chip" href={l.url} target="_blank" rel="noreferrer noopener">
                    {l.label}
                  </a>
                ))}
              </div>
            )}
          </>
        )}
      </header>

      {user && !editing && (
        <nav className="ownerbar" aria-label="Your Lost Lands">
          <Link to="/preferences" className="lead">
            Festival preferences
          </Link>
          <Link to="/plan">My plan</Link>
          <Link to="/lineup">My lineup</Link>
          <Link to="/photos">My photos</Link>
          <Link to="/settings">Account and data</Link>
        </nav>
      )}

      {user && !editing && (
        <section className="profile-crew" aria-label="Your groups">
          <h2>Your groups</h2>
          {crews.length > 0 ? (
            <>
              <p>
                Send an invitation by email or text message. Whoever opens it can make an account
                and join.
              </p>
              {crews.map((crew) => (
                <GroupInvitation key={crew.id} crew={crew} />
              ))}
            </>
          ) : (
            <p>
              You are not in a group yet. A group shares plans and shows where the weekend
              diverges.
            </p>
          )}
          <div className="profile-crew-actions">
            <Link to="/search#my-groups">
              {crews.length > 0 ? 'Manage groups' : 'Create or join a group'}
            </Link>
            <Link to="/search">Find people</Link>
          </div>
        </section>
      )}

      {accountReady && user && (
        <section className="panel">
          <h2>Your account is ready</h2>
          <p className="muted">
            Add profile details now, or head back to the planner. Your existing choices stay with
            this browser. Check saved-data status in Settings.
          </p>
          <div className="flex wrap">
            <button
              className="primary small"
              onClick={() => {
                setEditing(true);
                navigate('/profile', { replace: true });
              }}
            >
              Set up profile
            </button>
            <Link to="/plan" replace className="small ghost">
              {hasPlan ? 'Resume planning' : 'Start planning'}
            </Link>
          </div>
        </section>
      )}

      <div className="widgetgrid">
        {shown.map((w) => {
          const Panel = PANELS[w.id];
          if (!Panel) return null;
          const ctx: PanelContext = {
            widget: w,
            editing,
            user: user ?? null,
            page,
            photos,
            art,
            offers,
            artNote,
            setArtNote,
            reloadArt: loadArt,
            stats,
            activeDay,
            setLineupDay,
            setAbout: (markdown) => setPage({ ...page, aboutMd: markdown }),
            commit: () => void savePage(page),
            rename: (title) => mutate(w.id, (x) => ({ ...x, title })),
          };
          return (
            <WidgetShell
              key={w.id}
              widget={w}
              editing={editing}
              title={w.title || WIDGET_NAMES[w.id]}
              aside={
                w.id === 'lineup' ? (
                  <div className="daydots">
                    {derived.dayContexts.map((c) => (
                      <button
                        key={c.day.id}
                        className={activeDay === c.day.id ? 'on' : ''}
                        style={{ ['--day' as string]: dayColor(c.day.id) }}
                        title={c.day.label}
                        onClick={() => setLineupDay(c.day.id)}
                      >
                        {c.day.label.slice(0, 1)}
                      </button>
                    ))}
                  </div>
                ) : (
                  asideFor(w.id)
                )
              }
              onRename={(title) => mutate(w.id, (x) => ({ ...x, title }))}
              tools={{
                move: (by) => move(w.id, by),
                width: () => mutate(w.id, (x) => ({ ...x, w: next(WIDTHS, x.w) })),
                box: () => mutate(w.id, (x) => ({ ...x, style: next(STYLES, x.style) })),
                corner: () => mutate(w.id, (x) => ({ ...x, corner: next(CORNERS, x.corner) })),
                texture: () => mutate(w.id, (x) => ({ ...x, texture: next(TEXTURES, x.texture) })),
                opacity: () => mutate(w.id, (x) => ({ ...x, opacity: next(OPACITIES, x.opacity) })),
                hide: () =>
                  void savePage({ ...page, widgets: page.widgets.filter((x) => x.id !== w.id) }),
              }}
              drag={{
                active: dragging === w.id,
                over: dragOver === w.id,
                onStart: (e) => {
                  setDragging(w.id);
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', w.id);
                },
                onOver: (e) => {
                  if (!editing || !dragging || dragging === w.id) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  setDragOver(w.id);
                },
                onLeave: () => setDragOver((id) => (id === w.id ? null : id)),
                onDrop: (e) => {
                  e.preventDefault();
                  const from = dragging;
                  setDragging(null);
                  setDragOver(null);
                  if (!from || from === w.id) return;
                  const order = page.widgets.map((x) => x.id);
                  const moved = order.filter((id) => id !== from);
                  moved.splice(moved.indexOf(w.id), 0, from);
                  void savePage({
                    ...page,
                    widgets: moved.map((id) => page.widgets.find((x) => x.id === id)!),
                  });
                },
                onEnd: () => {
                  setDragging(null);
                  setDragOver(null);
                },
              }}
            >
              <Panel ctx={ctx} />
            </WidgetShell>
          );
        })}
      </div>
    </div>
  );
}
